import {useCallback} from 'react';
import {useOktaAuth} from '@okta/okta-react';
import {useQueryClient} from '@tanstack/react-query';

export const useOkta = () => {
  const {authState, oktaAuth} = useOktaAuth();
  const queryClient = useQueryClient();

  // Auth state
  const refreshToken = authState?.refreshToken?.refreshToken;
  const accessToken = authState?.accessToken?.accessToken;
  const error = authState?.error?.message;

  // Auth actions
  const login = useCallback(async () => {
    try {
      await oktaAuth.signInWithRedirect();
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  }, [oktaAuth]);

  const logout = useCallback(async () => {
    try {
      await oktaAuth.signOut();
      // Clear all TanStack Query caches
      queryClient.clear();
    } catch (error) {
      console.error('Logout failed:', error);
      throw error;
    }
  }, [oktaAuth, queryClient]);

  const refreshLogin = useCallback(async () => {
    try {
      const tokenManager = oktaAuth.tokenManager;
      await tokenManager.renew('accessToken');
      await tokenManager.renew('idToken');
    } catch (error) {
      console.error('Token refresh failed:', error);
      // If refresh fails, redirect to login
      await logout();
      throw error;
    }
  }, [oktaAuth, logout]);

  return {
    // Okta native
    authState,
    oktaAuth,

    // Simplified auth state
    token: accessToken, // Use accessToken for API calls
    refreshToken,
    accessToken,
    error,

    // Auth actions
    login,
    logout,
    refreshLogin,
  };
};
